import React, { useMemo, useState } from "react";
import { Meter, SortableHead } from "./StatsTable";
import { buildProfessionalRows, deriveMetrics } from "./beautyStatsMetrics";
import { nextSort, sortRows } from "./statsSort";
import { fmtHours, fmtInt, fmtMoney, fmtPct, fmtPct0 } from "./statsFormat";

const COLUMNS = [
  { key: "name", label: "Profesional", numeric: false },
  { key: "appointments", label: "Turnos", numeric: true },
  { key: "bookedHours", label: "Horas con turno", numeric: true },
  { key: "occupancy", label: "Ocupación de agenda", numeric: true },
  { key: "revenue", label: "Ingresos", numeric: true },
  { key: "ticket", label: "Ticket promedio", numeric: true },
  { key: "commission", label: "Comisión", numeric: true },
  { key: "noShowRate", label: "No asistió", numeric: true },
  { key: "linkShare", label: "Por su link", numeric: true },
];

/**
 * Por profesional tab: one row per professional with appointments, hours with an appointment,
 * occupancy (with a meter), revenue, average ticket, commission, no-show rate and the share of
 * their appointments that came through their own link. Every column sorts (the header is a button
 * and the `th` carries `aria-sort`); empty values always sort last. A total row closes the table
 * when more than one professional is shown.
 *
 * The total comes from the salon totals, so it also counts appointments of professionals that were
 * removed (they are not listed): a note says so when the rows do not add up.
 *
 * @param {{current: object}} props  `current` is a stats response.
 */
export default function BeautyProfesionalesTab({ current }) {
  const [sort, setSort] = useState({ key: "revenue", dir: -1 });
  const rows = useMemo(() => sortRows(buildProfessionalRows(current.perProfessional), sort.key, sort.dir), [current, sort]);
  const totals = deriveMetrics(current);
  const listedAppointments = rows.reduce((sum, row) => sum + row.appointments, 0);
  const includesRemoved = rows.length > 0 && totals.appointments > listedAppointments;

  return (
    <section className="stats-panel" aria-labelledby="stats-professionals-title">
      <div className="stats-panel__head">
        <div>
          <h2 className="stats-panel__title" id="stats-professionals-title">
            Rendimiento por profesional
          </h2>
          <p className="stats-panel__sub">Ordená por cualquier columna. La comisión sale de la configuración de Comisiones del salón.</p>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="stats-footnote">No hay profesionales para mostrar con estos filtros.</p>
      ) : (
        <>
          <div className="stats-table-wrap">
            <table>
              <SortableHead columns={COLUMNS} sort={sort} onSort={(key) => setSort((s) => nextSort(s, key))} />
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <span className="stats-court-name">{row.name}</span>
                      {row.specialty && <span className="stats-court-meta">{row.specialty}</span>}
                    </td>
                    <td className="num">{fmtInt(row.appointments)}</td>
                    <td className="num">{fmtHours(row.bookedHours)}</td>
                    <td className="num">
                      <Meter value={row.occupancy}>{fmtPct0(row.occupancy)}</Meter>
                    </td>
                    <td className="num">{fmtMoney(row.revenue)}</td>
                    <td className="num">{fmtMoney(row.ticket)}</td>
                    <td className="num">{fmtMoney(row.commission)}</td>
                    <td className="num">{fmtPct(row.noShowRate)}</td>
                    <td className="num">{fmtPct0(row.linkShare)}</td>
                  </tr>
                ))}
              </tbody>
              {rows.length > 1 && (
                <tfoot>
                  <tr>
                    <td>Total del salón</td>
                    <td className="num">{fmtInt(totals.appointments)}</td>
                    <td className="num">{fmtHours(totals.bookedHours)}</td>
                    <td className="num">{fmtPct0(totals.occupancy)}</td>
                    <td className="num">{fmtMoney(totals.revenue)}</td>
                    <td className="num">{fmtMoney(totals.ticket)}</td>
                    <td className="num">{fmtMoney(totals.commission)}</td>
                    <td className="num">{fmtPct(totals.noShowRate)}</td>
                    <td className="num">{fmtPct0(totals.viaLinkShare)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {includesRemoved && (
            <p className="stats-footnote">El total incluye turnos de profesionales dados de baja, que no aparecen en la lista.</p>
          )}
        </>
      )}
    </section>
  );
}
